import styled from '@emotion/native';
import type { ColorName } from '@emotion/react';
import { GroupSecurity, TaskCode } from '@quromedical/fhir-common';
import type { CreateMediaData } from '@quromedical/models';
import { Col, Row } from 'components/base';
import { WithGroup } from 'core/auth';
import {
  Assignment,
  type AssignmentFetcher,
  type AssignmentResult,
} from 'core/task';
import {
  Button,
  Card,
  Divider,
  type IconName,
  Spacer,
  Text,
  Visible,
} from 'design-system/base';
import { Grid } from 'design-system/layout';
import { BASE_SIZE, BORDER_RADIUS, MARGIN } from 'design-system/theme';
import { shortMonthDateFormatter, timeFormatter } from 'helpers';
import { useIsSmallerThan } from 'hooks/useResponsive';
import { useToggle } from 'hooks/useToggle';
import React, { isValidElement, type ReactElement } from 'react';

import { FileCard } from '../file-card';
import { Tag } from '../tag';
import { NoteUpdateForm } from './NoteUpdateForm';

export interface Measurement {
  name?: string;
  value: string | number;
  unit?: string;
}

export interface Attachment {
  name: string;
  onDownloadPress: () => Promise<void>;
}

export interface Note {
  title?: string;
  body?: string | JSX.Element;
  measurements?: Measurement[];
}

export interface TagConfig {
  text: string;
  color: ColorName;
  textColor?: ColorName;
}

export interface NoteAction {
  text?: string;
  icon?: IconName;
  color?: ColorName;
  onPress: () => void;
}

export interface NoteEdit {
  onEditSubmit: (subjective: string, media: CreateMediaData[]) => Promise<void>;
  canEdit: boolean;
}

export interface TaskAssignment {
  onSubmit: (result?: AssignmentResult | undefined) => void | Promise<void>;
  performers: AssignmentFetcher;
}

const isAttachmentConfig = (
  config: Attachment | ReactElement,
): config is Attachment => !isValidElement(config);

interface NoteCardProps {
  patientId: string;
  date?: Date;
  tags?: TagConfig[];
  notes: Note[];
  attachments?: (Attachment | ReactElement)[];
  createdBy?: string;
  action?: NoteAction;
  edit?: NoteEdit;
  taskAssignment: TaskAssignment | undefined;
}

interface SizeProps {
  isSmall: boolean;
}

const LeftSection = styled(Col)<SizeProps>(({ isSmall }) => ({
  width: isSmall ? undefined : BASE_SIZE[148],
}));

const TagWrapper = styled.View<SizeProps>(({ isSmall }) => ({
  marginTop: MARGIN['2xs'],
  marginRight: isSmall ? MARGIN['2xs'] : undefined,
  flexDirection: 'row',
  flexWrap: 'wrap',
}));

const NoteWrapper = styled.View({
  marginBottom: MARGIN['2xs'],
});

const Dot = styled.View(({ theme }) => ({
  height: BASE_SIZE[4],
  width: BASE_SIZE[4],
  marginHorizontal: MARGIN['2xs'],
  backgroundColor: theme.color['text-default'],
  borderRadius: BORDER_RADIUS.full,
}));

export const NoteCard: React.FC<NoteCardProps> = ({
  patientId,
  date,
  notes = [],
  attachments = [],
  tags = [],
  createdBy = '',
  action,
  edit,
  taskAssignment,
}) => {
  const [isFormToggled, toggleEditForm] = useToggle(false);

  const dateDisplay = shortMonthDateFormatter(date) || '';
  const timeDisplay = timeFormatter(date) || '';
  const isSmall = useIsSmallerThan('sm');

  const DirectionalView = isSmall ? Col : Row;
  const TagGroup = isSmall ? Row : Col;

  const dividerDirection = isSmall ? 'horizontal' : 'vertical';
  const alignMeasurements = isSmall ? undefined : 'center';

  return (
    <Card>
      <DirectionalView>
        <LeftSection isSmall={isSmall}>
          <Text numberOfLines={1} ellipsizeMode="head" variant="body-strong">
            {createdBy}
          </Text>
          <Text variant="body">{dateDisplay}</Text>
          <Text variant="caption">{timeDisplay}</Text>
          <TagGroup>
            {tags.map((tag) => (
              <TagWrapper isSmall={isSmall} key={tag.text}>
                <Tag
                  text={tag.text}
                  color={tag.color}
                  textColor={tag.textColor}
                />
              </TagWrapper>
            ))}
          </TagGroup>
        </LeftSection>
        <Divider orientation={dividerDirection} spacing="m" />
        <Col flex={1}>
          <Row justifyContent="flex-end">
            <Col>
              {taskAssignment && (
                <WithGroup groups={[GroupSecurity.TaskCreate]}>
                  <Assignment
                    patientId={patientId}
                    codes={[TaskCode.ClinicalImpressionFollowUp]}
                    onChange={taskAssignment.onSubmit}
                    iconOnly={true}
                  />
                </WithGroup>
              )}
            </Col>
            {edit ? (
              <Button
                size="large"
                variant="text"
                iconPosition="right"
                icon={'edit'}
                onPress={toggleEditForm}
              />
            ) : null}
            {action ? (
              <Button
                size="large"
                variant="text"
                iconPosition="right"
                textColor={action.color}
                text={action.text}
                icon={action.icon}
                onPress={action.onPress}
              />
            ) : null}
          </Row>
          {notes.map((note, noteIndex) => (
            <NoteWrapper key={noteIndex}>
              <Visible if={note.title}>
                <Text selectable variant="body-strong">
                  {note.title}
                </Text>
              </Visible>
              <Visible if={note.body}>
                {typeof note.body === 'string' ? (
                  <Text selectable variant="body">
                    {note.body}
                  </Text>
                ) : (
                  note.body
                )}
              </Visible>
              <DirectionalView alignItems={alignMeasurements} flexWrap="wrap">
                {note.measurements?.map((measurement, measureIndex, arr) => {
                  const isLast = arr.length - 1 === measureIndex;
                  const showDot = !isLast && !isSmall;

                  const text = `${measurement.name || ''}: ${measurement.value || ''}${
                    measurement.unit || ''
                  }`;

                  return (
                    <React.Fragment key={measureIndex}>
                      <Row>
                        <Text selectable variant="body">
                          {text}
                        </Text>
                      </Row>
                      <Visible if={showDot}>
                        <Dot />
                      </Visible>
                    </React.Fragment>
                  );
                })}
              </DirectionalView>
            </NoteWrapper>
          ))}
          <Grid sm={1} md={2} lg={3} xl={4} gap="xs">
            {attachments?.map((attachment, attachmentIndex) => {
              const isConfig = isAttachmentConfig(attachment);
              if (!isConfig) {
                return attachment;
              }

              return (
                <FileCard
                  key={attachmentIndex}
                  name={attachment.name}
                  onPress={attachment.onDownloadPress}
                />
              );
            })}
          </Grid>
          <Spacer height={MARGIN['2xs']} />
          {isFormToggled && edit?.onEditSubmit ? (
            <NoteUpdateForm
              id={patientId}
              onEditSubmit={edit.onEditSubmit}
              toggleEditForm={toggleEditForm}
            />
          ) : null}
        </Col>
      </DirectionalView>
    </Card>
  );
};
